import { createContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrderAdminService,
  createProductOrderAdminService,
  deleteOrderAdminService,
  editOrderAdminService,
  getProductsAdminService,
  getUsersAdminService,
} from "../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { setMetaAction } from "./../../../redux/actions/metaActions/metaActions";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import { isEmpty } from "lodash";
import { userRoleItems } from "../../../utils/enum";
import { calculateTotalTableAmount, numberSeparate } from "../../../utils";

export const dashboardAdminContext = createContext({
  order: {},
  setOrder: () => {},
  table: {},
  setTable: () => {},
  isProductOrder: {},
  setIsProductOrder: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetProducts: () => {},
  handleCreateOrder: () => {},
  handleEditOrder: () => {},
  handleDeleteOrder: () => {},
  checkExistOrder: () => {},
});

const DashboardAdminContext = ({ children }) => {
  const meta = useSelector((state) => state.meta);

  const dispatch = useDispatch();

  const [order, setOrder] = useState({});
  const [table, setTable] = useState({});
  const [isProductOrder, setIsProductOrder] = useState(false);

  // Modal Operation
  const handleShowModal = (tbl, val, setValue) => {
    setTable(tbl);

    if (val) {
      setOrder(val);
      setValue("table", val?.table);
      setValue("table.cue", val?.table?.cue?.count);
      setValue("products", val?.products);
      setValue("deductWallet", val?.pricing?.deductWallet);
      setValue("description", val?.description);
      setValue("isComplete", false);
      setValue(
        "totalTableAmount",
        calculateTotalTableAmount(
          val?.startDate,
          val?.table?.price,
          val?.table?.cue?.count,
          val?.table?.cue?.percent
        )
      );
      setValue("user", {
        value: val?.user?._id,
        label: `${val?.user?.firstName} ${val?.user?.lastName} - ${val?.user?.phoneNumber}`,
      });
    }
  };
  const handleHideModal = () => {
    setTable({});
    setOrder({});
    setIsProductOrder(false);
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
      role: userRoleItems[1],
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} - ${item.lastName} - ${item.phoneNumber}`,
              data: item,
            }))
          );
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Products
  const handleGetProducts = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProductsAdminService(objData);
      if (status === 200) {
        callback &&
          callback(
            data.result?.products?.map((item) => ({
              value: item._id,
              label: `${item.title} - ${numberSeparate(item.price)} تومان`,
              data: item,
            }))
          );
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Order
  const handleCreateOrder = async (userData) => {
    const objData = {
      ...userData,
      user: userData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createOrderAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        dispatch(setMetaAction());
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Product Order
  const handleCreateProductOrder = async (userData) => {
    const objData = {
      ...userData,
      user: userData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createProductOrderAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        dispatch(setMetaAction());
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Order
  const handleEditOrder = async (userData) => {
    const objData = {
      ...userData,
      id: order._id,
      user: userData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editOrderAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        dispatch(setMetaAction());
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Order
  const handleDeleteOrder = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteOrderAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        dispatch(setMetaAction());
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Check Exist Order
  const checkExistOrder = (id) => {
    const order = meta.openOrders?.find((_o) => _o.table?._id === id);
    const isExist = !isEmpty(order);

    return { isExist, order };
  };

  return (
    <dashboardAdminContext.Provider
      value={{
        order,
        setOrder,
        table,
        setTable,
        isProductOrder,
        setIsProductOrder,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetProducts,
        handleCreateOrder,
        handleCreateProductOrder,
        handleEditOrder,
        handleDeleteOrder,
        checkExistOrder,
      }}
    >
      {children}
    </dashboardAdminContext.Provider>
  );
};

export default DashboardAdminContext;
